import ListItem from "@mui/material/ListItem";
import cx from "classnames";
import { DataStatusBadge } from "components-redesign/DataStatusBadge/DataStatusBadge";
import { Tooltip } from "components-redesign/Tooltip/Tooltip";
import TooltipContext from "components-redesign/Tooltip/TooltipContext";
import { AppNotificationProvider } from "components/AppNotifications/AppNotificationProvider";
import { AppNotifications } from "components/AppNotifications/AppNotifications";
import { FlexContent } from "components/FlexContent/FlexContent";
import { FeatureToggles } from "configuration";
import { isDevCluster, isDevClusterAlpha } from "configuration/endpoints";
import { ChangeHistory } from "features/ChangeHistory/ChangeHistory";
import React, { useContext } from "react";
import { useAccountId, useOrganizationId, useUsername } from "redux/auth";
import AllDownloadsButton from "../Navbar/AllDownloadsStatusButton";
import { useInfoMenuStyles } from "./styles";

const FEATURE_TOGGLE_USERS_WHITELIST = [
  "eduardo@dataseat.com",
  "paul@dataseat.com",
  "jason@dataseat.com",
  "rich@dataseat.com",
  "olha@dataseat.com",
  "subhash.nottath@verve.com",
  "andy@dataseat.com",
  "juan@dataseat.com",
  "nikolay.zhdanov@dataseat.com",
  "hanna.beliayeva@verve.com",
  "yurii.haievkyi@match2one.com",
  "alexei.moltchan@verve.com",
  "iryna.ratushna@match2one.com",
  "maria.movcharenko@match2one.com",
  "nazarii.tuchapets@match2one.com",
  "luke@dataseat.com",
  "ieva.balode@verve.com",
  "matina.thomaidou@dataseat.com",
  "marcos@dataseat.com",
];

export enum InfoItemName {
  FeatureToggles = "feature-toggles",
  DataDelayStatus = "data-delay-status",
  Downloads = "downloads",
  Notifications = "notifications",
  ChangeHistory = "change-history",
}

type TInfoMenuItem = {
  name: InfoItemName;
  component: React.ReactNode;
  tooltip: string;
};

function InfoItemsMenu({ rootPath }: { rootPath?: string }) {
  const username = useUsername();
  const organizationId = useOrganizationId();
  const accountId = useAccountId();
  const { classes } = useInfoMenuStyles();
  const showFeatureToggle =
    process.env.NODE_ENV === "development" ||
    isDevCluster ||
    isDevClusterAlpha ||
    FEATURE_TOGGLE_USERS_WHITELIST.includes(username);
  const showDataDelayedBadge = ["/campaigns", "/analytics"].includes(
    rootPath ?? ""
  );
  const [activeItemName, setActiveItemName] = React.useState<InfoItemName>();

  const infoMenuItems: TInfoMenuItem[] = React.useMemo(() => {
    const featureToggleItem = showFeatureToggle
      ? [
          {
            name: InfoItemName.FeatureToggles,
            component: (
              <FeatureToggles
                onActive={(active) =>
                  setActiveItemName(
                    active ? InfoItemName.FeatureToggles : undefined
                  )
                }
              />
            ),
            tooltip: "Feature Toggles",
          },
        ]
      : [];

    const dataDelayedItem = showDataDelayedBadge
      ? [
          {
            name: InfoItemName.DataDelayStatus,
            component: (
              <DataStatusBadge
                navbar
                onActive={(active) =>
                  setActiveItemName(
                    active ? InfoItemName.DataDelayStatus : undefined
                  )
                }
              />
            ),
            tooltip: "Data Updates",
          },
        ]
      : [];

    const changeHistoryItem = [
      {
        name: InfoItemName.ChangeHistory,
        component: <ChangeHistory />,
        tooltip: "Change History",
      },
    ];

    const items = [
      ...featureToggleItem,
      ...dataDelayedItem,
      ...changeHistoryItem,
      {
        name: InfoItemName.Downloads,
        component: (
          <AllDownloadsButton
            onActive={(active) =>
              setActiveItemName(active ? InfoItemName.Downloads : undefined)
            }
          />
        ),
        tooltip: "Downloads",
      },
      {
        name: InfoItemName.Notifications,
        component: (
          <AppNotificationProvider>
            <AppNotifications
              onActive={(active) =>
                setActiveItemName(
                  active ? InfoItemName.Notifications : undefined
                )
              }
            />
          </AppNotificationProvider>
        ),
        tooltip: "Notifications",
      },
    ];

    return items;
  }, [showFeatureToggle, showDataDelayedBadge]);

  const { openedTooltipId, handleTooltipTrigger, removeTooltipById } =
    useContext(TooltipContext);

  if (!organizationId || !accountId) {
    return null;
  }

  return (
    <>
      {infoMenuItems.map((item) => (
        <Tooltip
          disableListeners
          open={openedTooltipId === item.name}
          key={item.name}
          title={item.tooltip}
          placement="right"
          slotProps={{
            popper: {
              modifiers: [
                {
                  name: "offset",
                  options: {
                    offset: [0, 12],
                  },
                },
              ],
            },
          }}>
          <ListItem
            onMouseEnter={handleTooltipTrigger(item.name)}
            onMouseLeave={removeTooltipById(item.name)}
            className={cx(classes.infoItem, {
              [classes.infoItemActive]: activeItemName === item.name,
            })}>
            <FlexContent
              fullWidth={false}
              align="center"
              justify="center"
              className={classes.infoItemBg}>
              {item.component}
            </FlexContent>
          </ListItem>
        </Tooltip>
      ))}
    </>
  );
}

export default InfoItemsMenu;
